/**
 * Content-art URL resolution. Generated renders are keyed by content id and
 * served from `/content/<id>.png` — fetched from R2 into `dist/content/` by the
 * postbuild step (`scripts/fetch-content-assets.mjs`), so they exist on deployed
 * builds. In local dev the files usually aren't present; consumers must degrade
 * gracefully (the overlay hides broken icons, the scene keeps its procedural
 * backdrop). View models carry content ids directly, so every entity's icon is
 * just `contentArtUrl(entity.id)`.
 */
import type { Age } from '@content/index.js';
import type { ParallaxOptions } from '@render/index.js';

export function contentArtUrl(contentId: string): string {
  return `/content/${contentId}.png`;
}

/**
 * The age's 9-slice frame sheet URL. The sheet is generated from `age.uiPrompt`
 * under a synthetic id — keep this derivation in sync with the art pipeline's
 * discovery (`scripts/content-assets.mjs`).
 */
export function uiFrameArtUrl(ageId: string): string {
  return contentArtUrl(`ui.${ageId.replace(/^age\./, '')}.frame`);
}

/** An age's parallax sprite layers (content-schema §2 `background.sprites`), ids →
 * art URLs. The full-bleed backdrop itself is the age's own `scene` image (`age.id`),
 * rendered separately as the scene's plain backdrop sprite — these are the foreground
 * cutouts layered above it. */
export function parallaxBackground(age: Age): ParallaxOptions | undefined {
  if (age.background === undefined) return undefined;
  return {
    sprites: age.background.sprites.map((sprite) => ({
      url: contentArtUrl(sprite.id),
      z: sprite.z,
    })),
  };
}
